import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { usePublicProfile } from '@/hooks/usePublicProfile'
import { ThemeProvider } from '@/themes/ThemeProvider'
import ModernTemplate from '@/themes/templates/ModernTemplate'
import ClassicTemplate from '@/themes/templates/ClassicTemplate'
import MinimalTemplate from '@/themes/templates/MinimalTemplate'
import CreativeTemplate from '@/themes/templates/CreativeTemplate'
import ProfileSkeleton from '@/components/profile/ProfileSkeleton'
import ProfileNotFound from '@/components/profile/ProfileNotFound'
import { generateProfileMetaTags } from '@/utils/seo'
import { getProfileSettings } from '@/utils/profileDefaults'
import type { ThemeType } from '@/types'

/**
 * Select the appropriate template component based on the theme
 */
function getTemplateComponent(theme: ThemeType) {
  const templates = {
    modern: ModernTemplate,
    classic: ClassicTemplate,
    minimal: MinimalTemplate,
    creative: CreativeTemplate,
  }
  return templates[theme] || ModernTemplate
}

export default function CandidateProfilePage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: profile, isLoading, isError } = usePublicProfile(slug)

  // Loading state
  if (isLoading) {
    return <ProfileSkeleton />
  }

  // Error or not found state
  if (isError || !profile) {
    return <ProfileNotFound username={slug} />
  }

  // Ensure profile settings have defaults
  const profileSettings = getProfileSettings(profile.profileSettings)

  // Generate SEO meta tags
  const metaTags = generateProfileMetaTags(profile)

  // Select the appropriate template based on theme
  const TemplateComponent = getTemplateComponent(profileSettings.theme)

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />

        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:type" content="profile" />
        {metaTags.url && <meta property="og:url" content={metaTags.url} />}
        {metaTags.image && <meta property="og:image" content={metaTags.image} />}

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        {metaTags.image && <meta name="twitter:image" content={metaTags.image} />}
      </Helmet>

      {/* Profile with Theme */}
      <ThemeProvider
        theme={profileSettings.theme}
        colorScheme={profileSettings.colorScheme}
        animationsEnabled={profileSettings.customization.animationsEnabled}
      >
        <TemplateComponent profile={{ ...profile, profileSettings }} />
      </ThemeProvider>
    </>
  )
}
